/**
 * Statistics dashboard — Roomgrid operational overview built on ONE
 * aggregated fetch (`/account/statistics-overview`):
 *
 *   controls   period (Today / 7d / 30d / Custom) + channel select
 *   KPIs       sent / success rate / failed / active accounts, with
 *              deltas vs the equivalent previous window
 *   chart      deliveries per day, stacked by channel, red cap = failed
 *   panels     by-channel breakdown · top accounts (send-API audit)
 *   API health accounts×endpoints WITH errors; the full legacy audit
 *              table stays behind «View all API statistics»
 */

import React from "react";
import { useNotify, useTranslate } from "react-admin";
import {
  Box,
  Button,
  Collapse,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { ChannelChip } from "@/components/common";
import { Autocomplete } from "@mui/material";
import { getAllAccounts } from "@/features/test/services/testService";
import type { AccountListItem } from "@/features/test/types/notificationTest";
import {
  getStatisticsOverview,
  type StatisticsOverview,
} from "../services/statisticsService";
import ApiStatisticsTable from "./StatisticsList";

const i18n = "resources.statistics.dashboard";

type PeriodKey = "today" | "7d" | "30d" | "custom";

const CHANNEL_ORDER = [
  "email",
  "whatsapp",
  "sms",
  "telegram",
  "slack",
  "teams",
  "discord",
  "googlechat",
  "wecom",
  "mattermost",
  "rocketchat",
  "viber",
  "line",
  "inbox",
  "webhook",
  "webpush",
  "apppush",
  "other",
];

const isoDay = (d: Date): string => d.toISOString().slice(0, 10);

/** Every header control locks to the same 40px input height. */
export const controlInputSx = {
  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
    height: 40,
    minHeight: 40,
  },
  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiOutlinedInput-input": {
    py: 0,
  },
} as const;

export const fmtMs = (ms: number | null | undefined): string => {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} s`;
  return `${(ms / 60000).toLocaleString(undefined, { maximumFractionDigits: 1 })} min`;
};

const periodRange = (key: PeriodKey): { from: string; to: string } => {
  const today = new Date();
  const to = isoDay(today);
  if (key === "today") return { from: to, to };
  const days = key === "30d" ? 29 : 6;
  const from = new Date(today.getTime() - days * 86400000);
  return { from: isoDay(from), to };
};

const useChannelColors = (): Record<string, string> => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  return {
    email: rg.accent,
    whatsapp: "#25D366",
    sms: rg.violet.fg,
    telegram: "#26A5E4",
    slack: "#E01E5A",
    teams: "#6264A7",
    discord: "#5865F2",
    googlechat: "#00AC47",
    wecom: "#07C160",
    mattermost: "#0058CC",
    rocketchat: "#F5455C",
    viber: "#7360F2",
    line: "#06C755",
    inbox: rg.accent,
    webhook: rg.neutral.fg,
    webpush: rg.warn.fg,
    apppush: rg.neutral.fg,
    other: rg.text4,
  };
};

/* ============================== KPI card ============================== */

export const KpiCard: React.FC<{
  label: string;
  value: string;
  valueColor?: string;
  delta?: { text: string; tone: "good" | "bad" | "flat" };
}> = ({ label, value, valueColor, delta }) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  const toneColor =
    delta?.tone === "good"
      ? rg.ok.line
      : delta?.tone === "bad"
        ? rg.danger.line
        : rg.text3;
  return (
    <Box
      sx={{
        backgroundColor: rg.bgElev,
        border: `1px solid ${rg.border}`,
        borderRadius: "14px",
        p: "16px 18px",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: rg.text3,
          mb: "6px",
        }}
      >
        {label}
      </Typography>
      <Typography
        className="rg-mono"
        sx={{
          fontSize: "26px",
          fontWeight: 700,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          color: valueColor,
        }}
      >
        {value}
      </Typography>
      {delta && (
        <Typography sx={{ fontSize: "11.5px", fontWeight: 600, mt: "3px", color: toneColor }}>
          {delta.text}
        </Typography>
      )}
    </Box>
  );
};

/* ============================ stacked chart =========================== */

const DailyChart: React.FC<{ data: StatisticsOverview }> = ({ data }) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  const colors = useChannelColors();
  const days = data.daily;
  const max = Math.max(1, ...days.map((d) => d.total));

  if (days.length === 0) {
    return (
      <Typography sx={{ py: 4, textAlign: "center", fontSize: "12.5px", color: rg.text3 }}>
        —
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "5px", height: 150 }}>
        {days.map((d) => {
          const okShare = d.total === 0 ? 0 : (d.total - d.failed) / d.total;
          const channels = CHANNEL_ORDER.filter((c) => (d.byChannel[c] ?? 0) > 0);
          const channelTotal = channels.reduce(
            (acc, c) => acc + (d.byChannel[c] ?? 0),
            0,
          );
          const tooltip = [
            `${d.date} — ${d.total}`,
            ...channels.map((c) => `${c}: ${d.byChannel[c]}`),
            d.failed > 0 ? `failed: ${d.failed}` : null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <Tooltip key={d.date} title={tooltip}>
              <Box
                sx={{
                  flex: 1,
                  height: `${Math.max(4, (d.total / max) * 100)}%`,
                  display: "flex",
                  flexDirection: "column-reverse",
                  gap: "1px",
                  minWidth: 0,
                }}
              >
                {channels.map((c) => (
                  <Box
                    key={c}
                    sx={{
                      flexGrow: ((d.byChannel[c] ?? 0) / channelTotal) * okShare,
                      backgroundColor: colors[c],
                      borderRadius: "2px",
                      minHeight: "2px",
                    }}
                  />
                ))}
                {d.failed > 0 && (
                  <Box
                    sx={{
                      flexGrow: 1 - okShare,
                      backgroundColor: rg.danger.line,
                      borderRadius: "2px",
                      minHeight: "3px",
                      order: 1,
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", gap: "5px", mt: "6px" }}>
        {days.map((d) => (
          <Typography
            key={d.date}
            className="rg-mono"
            sx={{
              flex: 1,
              textAlign: "center",
              fontSize: "9.5px",
              color: rg.text3,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {days.length > 12 ? d.date.slice(8) : d.date.slice(5)}
          </Typography>
        ))}
      </Box>
    </>
  );
};

/* ============================== panels ================================ */

export const PanelBox: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  return (
    <Box
      sx={{
        backgroundColor: rg.bgElev,
        border: `1px solid ${rg.border}`,
        borderRadius: "14px",
        p: "18px",
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>{title}</Typography>
      {subtitle && (
        <Typography sx={{ fontSize: "11.5px", color: rg.text3, mb: "12px" }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  );
};

const cellSx = {
  fontSize: "12.5px",
  py: "7px",
  px: "10px",
} as const;

const headSx = {
  fontSize: "10.5px",
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  py: "6px",
  px: "10px",
  textAlign: "left" as const,
};

const SuccessBar: React.FC<{ rate: number | null }> = ({ rate }) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 60,
          height: 5,
          borderRadius: "99px",
          backgroundColor: rg.border,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${rate ?? 0}%`,
            height: "100%",
            borderRadius: "99px",
            backgroundColor:
              rate != null && rate < 90 ? rg.danger.line : rg.accent,
          }}
        />
      </Box>
      <Typography className="rg-mono" sx={{ fontSize: "12px", minWidth: 46, textAlign: "right" }}>
        {rate == null ? "—" : `${rate.toLocaleString()}%`}
      </Typography>
    </Box>
  );
};

const StatusPill: React.FC<{ requests: number; errors: number }> = ({
  requests,
  errors,
}) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  const translate = useTranslate();
  const pct = requests === 0 ? 0 : (100 * errors) / requests;
  const triplet = pct >= 5 ? rg.danger : pct >= 1 ? rg.warn : rg.ok;
  const label =
    pct >= 1
      ? translate(`${i18n}.failPct`, {
          pct: pct.toFixed(0),
          _: `${pct.toFixed(0)}% errors`,
        })
      : "OK";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        height: 20,
        px: "8px",
        borderRadius: "99px",
        backgroundColor: triplet.bg,
        color: triplet.fg,
        fontSize: "10.5px",
        fontWeight: 600,
      }}
    >
      <Box
        component="span"
        sx={{ width: 5, height: 5, borderRadius: "99px", backgroundColor: "currentColor" }}
      />
      {label}
    </Box>
  );
};

/* ============================== page ================================== */

export const StatisticsDashboard: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const colors = useChannelColors();

  const [period, setPeriod] = React.useState<PeriodKey>("7d");
  const [customFrom, setCustomFrom] = React.useState("");
  const [customTo, setCustomTo] = React.useState("");
  const [channel, setChannel] = React.useState("");
  const [account, setAccount] = React.useState<AccountListItem | null>(null);
  const [accounts, setAccounts] = React.useState<AccountListItem[]>([]);
  const [data, setData] = React.useState<StatisticsOverview | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [showApiTable, setShowApiTable] = React.useState(false);

  React.useEffect(() => {
    getAllAccounts()
      .then(setAccounts)
      .catch(() => setAccounts([]));
  }, []);

  const range =
    period === "custom"
      ? customFrom && customTo
        ? { from: customFrom, to: customTo }
        : null
      : periodRange(period);

  React.useEffect(() => {
    if (!range) return;
    let cancelled = false;
    setLoading(true);
    getStatisticsOverview(range.from, range.to, channel || undefined, account?.id)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          notify(
            extractErrorMessage(err, "ra.notification.http_error", translate),
            { type: "error" },
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.from, range?.to, channel, account?.id]);

  const t = (key: string, fallback: string, args?: Record<string, unknown>) =>
    translate(`${i18n}.${key}`, { _: fallback, ...args });

  const deltaOf = (
    now: number,
    prev: number,
    higherIsBetter: boolean,
  ): { text: string; tone: "good" | "bad" | "flat" } => {
    const diff = now - prev;
    if (diff === 0) return { text: t("deltaFlat", "▬ no change"), tone: "flat" };
    const arrow = diff > 0 ? "▲" : "▼";
    const tone = diff > 0 === higherIsBetter ? "good" : "bad";
    const pct = prev > 0 ? ` (${Math.abs(Math.round((100 * diff) / prev))}%)` : "";
    return {
      text: `${arrow} ${Math.abs(diff).toLocaleString()}${pct} ${t("deltaVsPrev", "vs. previous period")}`,
      tone,
    };
  };

  const kpis = data?.kpis;

  const periodButtons: Array<{ key: PeriodKey; label: string }> = [
    { key: "today", label: t("periodToday", "Today") },
    { key: "7d", label: t("period7d", "7 days") },
    { key: "30d", label: t("period30d", "30 days") },
    { key: "custom", label: t("periodCustom", "Custom") },
  ];

  return (
    <Box sx={{ maxWidth: 1180, pb: 4 }}>
      {/* ---------------- header ---------------- */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary", ml: 2 }}>
          {translate("resources.statistics.name", { smart_count: 2, _: "Statistics" })}
        </Typography>
        {/* Bottom-aligned: the selects carry their label ABOVE the input
            (Roomgrid theme), so everything lines up on the input row. Every
            control is exactly 40px tall. Order: channel · account · period,
            with a RESERVED slot on the right so picking «Custom» never
            shifts the other controls. */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            label={t("channel", "Channel")}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            sx={{ width: 150, ...controlInputSx }}
          >
            <MenuItem value="">{t("allChannels", "All channels")}</MenuItem>
            {CHANNEL_ORDER.filter((c) => c !== "other").map((c) => (
              <MenuItem key={c} value={c}>
                {c === "webpush" ? "Web Push" : c === "apppush" ? "App Push" : c === "googlechat" ? "Google Chat" : c === "wecom" ? "WeCom" : c === "rocketchat" ? "Rocket.Chat" : c === "line" ? "LINE" : c[0].toUpperCase() + c.slice(1)}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            size="small"
            options={accounts}
            getOptionLabel={(a) => a.accountName}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={account}
            onChange={(_e, v) => setAccount(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("account", "Account")}
                placeholder={t("allAccounts", "All accounts")}
                InputLabelProps={{ shrink: true }}
              />
            )}
            sx={{
              width: 210,
              ...controlInputSx,
              // The empty state reads exactly like the channel select's
              // "All channels" value, not like a washed-out placeholder.
              "& input::placeholder": { color: "text.primary", opacity: 1 },
            }}
          />
          {/* Same anatomy as the labeled fields (micro label + 40px
              control) so the row aligns exactly, whatever the flex does. */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontSize: "10.5px",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: rg.text3,
                mb: "5px",
              }}
            >
              {t("period", "Period")}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "stretch",
                height: 40,
                border: `1px solid ${rg.border}`,
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: rg.bgElev,
              }}
            >
              {periodButtons.map((p, i) => (
              <Box
                key={p.key}
                component="button"
                onClick={() => setPeriod(p.key)}
                sx={{
                  border: "none",
                  borderRight:
                    i < periodButtons.length - 1 ? `1px solid ${rg.border}` : "none",
                  padding: "0 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  backgroundColor: period === p.key ? rg.text : "transparent",
                  color: period === p.key ? rg.bg : rg.text2,
                }}
              >
                {p.label}
              </Box>
            ))}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-end",
              width: { xs: "auto", md: 316 },
              minHeight: period === "custom" ? "auto" : 0,
            }}
          >
            {period === "custom" && (
              <>
                <TextField
                  type="date"
                  size="small"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  label={t("from", "From")}
                  sx={{ width: 154, ...controlInputSx }}
                />
                <TextField
                  type="date"
                  size="small"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  label={t("to", "To")}
                  sx={{ width: 154, ...controlInputSx }}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* ---------------- KPIs ---------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
          gap: "14px",
          mb: "20px",
        }}
      >
        {loading || !kpis ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={104} sx={{ borderRadius: "14px" }} />
          ))
        ) : (
          <>
            <KpiCard
              label={t("kpiSent", "Deliveries")}
              value={kpis.totalSent.toLocaleString()}
              delta={deltaOf(kpis.totalSent, kpis.prevTotalSent, true)}
            />
            <KpiCard
              label={t("kpiSuccessRate", "Success rate")}
              value={kpis.successRate == null ? "—" : `${kpis.successRate.toLocaleString()}%`}
              delta={
                kpis.successRate != null && kpis.prevSuccessRate != null
                  ? deltaOf(
                      Math.round(kpis.successRate * 10),
                      Math.round(kpis.prevSuccessRate * 10),
                      true,
                    )
                  : undefined
              }
            />
            <KpiCard
              label={t("kpiFailed", "Failed")}
              value={kpis.failed.toLocaleString()}
              valueColor={kpis.failed > 0 ? rg.danger.line : undefined}
              delta={deltaOf(kpis.failed, kpis.prevFailed, false)}
            />
            <KpiCard
              label={t("kpiActiveAccounts", "Active accounts")}
              value={kpis.activeAccounts.toLocaleString()}
              delta={deltaOf(kpis.activeAccounts, kpis.prevActiveAccounts, true)}
            />
            <KpiCard
              label={t("kpiProcessing", "Processing time")}
              value={fmtMs(kpis.avgProcessingMs)}
              delta={
                kpis.p95ProcessingMs != null
                  ? {
                      text: t("kpiProcessingP95", "p95 %{p95}", {
                        p95: fmtMs(kpis.p95ProcessingMs),
                      }),
                      tone: "flat",
                    }
                  : undefined
              }
            />
          </>
        )}
      </Box>

      {/* ---------------- daily chart ---------------- */}
      <PanelBox
        title={t("chartTitle", "Deliveries per day and channel")}
        subtitle={t("chartSubtitle", "Stacked by channel; the red cap is that day's failures")}
      >
        {loading || !data ? (
          <Skeleton variant="rounded" height={150} />
        ) : (
          <>
            <DailyChart data={data} />
            <Box sx={{ display: "flex", gap: "14px", flexWrap: "wrap", mt: "12px" }}>
              {CHANNEL_ORDER.filter(
                (c) => c !== "other" && data.daily.some((d) => (d.byChannel[c] ?? 0) > 0),
              ).map((c) => (
                <Box
                  key={c}
                  sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: rg.text2 }}
                >
                  <Box sx={{ width: 9, height: 9, borderRadius: "2px", backgroundColor: colors[c] }} />
                  {c === "webpush" ? "Web Push" : c === "apppush" ? "App Push" : c === "googlechat" ? "Google Chat" : c === "wecom" ? "WeCom" : c === "rocketchat" ? "Rocket.Chat" : c === "line" ? "LINE" : c[0].toUpperCase() + c.slice(1)}
                </Box>
              ))}
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: rg.text2 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: "2px", backgroundColor: rg.danger.line }} />
                {t("legendFailed", "Failed")}
              </Box>
            </Box>
          </>
        )}
      </PanelBox>

      <Box sx={{ height: 20 }} />

      {/* ---------------- two panels ---------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: "20px",
          mb: "20px",
        }}
      >
        <PanelBox
          title={t("byChannelTitle", "By channel")}
          subtitle={t("byChannelSubtitle", "Volume and success rate for the period")}
        >
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr" sx={{ "& th": { ...headSx, color: rg.text3, borderBottom: `1px solid ${rg.border}` } }}>
                <th>{t("colChannel", "Channel")}</th>
                <th style={{ textAlign: "right" }}>{t("colSent", "Sent")}</th>
                <th>{t("colSuccess", "Success")}</th>
                <th style={{ textAlign: "right" }}>{t("colProcessing", "Processing")}</th>
                <th style={{ textAlign: "right" }}>{t("colDelivery", "Delivery")}</th>
              </Box>
            </Box>
            <Box component="tbody">
              {(data?.byChannel ?? []).map((row) => (
                <Box component="tr" key={row.channel} sx={{ "& td": { ...cellSx, borderBottom: `1px solid ${rg.border}` }, "&:last-child td": { borderBottom: "none" } }}>
                  <td><ChannelChip channel={row.channel} /></td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>{row.sent.toLocaleString()}</td>
                  <td><SuccessBar rate={row.successRate} /></td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>{fmtMs(row.avgProcessingMs)}</td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>{fmtMs(row.avgDeliveryMs)}</td>
                </Box>
              ))}
              {!loading && (data?.byChannel ?? []).length === 0 && (
                <Box component="tr"><td colSpan={5} style={{ ...cellSx, color: rg.text3, textAlign: "center" }}>—</td></Box>
              )}
            </Box>
          </Box>
        </PanelBox>

        <PanelBox
          title={t("topAccountsTitle", "Top accounts")}
          subtitle={t("topAccountsSubtitle", "Send API requests in the period (audit)")}
        >
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr" sx={{ "& th": { ...headSx, color: rg.text3, borderBottom: `1px solid ${rg.border}` } }}>
                <th>{t("colAccount", "Account")}</th>
                <th style={{ textAlign: "right" }}>{t("colRequests", "Requests")}</th>
                <th style={{ textAlign: "right" }}>{t("colErrors", "Errors")}</th>
                <th>{t("colStatus", "Status")}</th>
              </Box>
            </Box>
            <Box component="tbody">
              {(data?.topAccounts ?? []).map((row) => (
                <Box component="tr" key={row.accountId} sx={{ "& td": { ...cellSx, borderBottom: `1px solid ${rg.border}` }, "&:last-child td": { borderBottom: "none" } }}>
                  <td>{row.accountName}</td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>{row.requests.toLocaleString()}</td>
                  <td className="rg-mono" style={{ textAlign: "right" }}>{row.errors.toLocaleString()}</td>
                  <td><StatusPill requests={row.requests} errors={row.errors} /></td>
                </Box>
              ))}
              {!loading && (data?.topAccounts ?? []).length === 0 && (
                <Box component="tr"><td colSpan={4} style={{ ...cellSx, color: rg.text3, textAlign: "center" }}>—</td></Box>
              )}
            </Box>
          </Box>
        </PanelBox>
      </Box>

      {/* ---------------- API health ---------------- */}
      <PanelBox
        title={t("apiHealthTitle", "API health")}
        subtitle={t(
          "apiHealthSubtitle",
          "Only accounts/endpoints with errors in the period",
        )}
      >
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box component="thead">
            <Box component="tr" sx={{ "& th": { ...headSx, color: rg.text3, borderBottom: `1px solid ${rg.border}` } }}>
              <th>{t("colAccount", "Account")}</th>
              <th>{t("colEndpoint", "Endpoint")}</th>
              <th style={{ textAlign: "right" }}>{t("colErrors24h", "Errors 24h")}</th>
              <th style={{ textAlign: "right" }}>{t("colErrorsPeriod", "Errors in period")}</th>
              <th style={{ textAlign: "right" }}>{t("colOkPeriod", "OK in period")}</th>
            </Box>
          </Box>
          <Box component="tbody">
            {(data?.apiErrors ?? []).map((row, i) => (
              <Box component="tr" key={`${row.accountName}-${row.endpoint}-${i}`} sx={{ "& td": { ...cellSx, borderBottom: `1px solid ${rg.border}` }, "&:last-child td": { borderBottom: "none" } }}>
                <td>{row.accountName}</td>
                <td className="rg-mono">{row.endpoint}</td>
                <td className="rg-mono" style={{ textAlign: "right", color: row.errorsLast24h > 0 ? rg.danger.line : undefined }}>
                  {row.errorsLast24h.toLocaleString()}
                </td>
                <td className="rg-mono" style={{ textAlign: "right" }}>{row.errorsInPeriod.toLocaleString()}</td>
                <td className="rg-mono" style={{ textAlign: "right" }}>{row.okInPeriod.toLocaleString()}</td>
              </Box>
            ))}
            {!loading && (data?.apiErrors ?? []).length === 0 && (
              <Box component="tr">
                <td colSpan={5} style={{ ...cellSx, color: rg.text3, textAlign: "center" }}>
                  {t("apiHealthEmpty", "No API errors in this period 🎉")}
                </td>
              </Box>
            )}
          </Box>
        </Box>
        <Button
          variant="text"
          onClick={() => setShowApiTable((v) => !v)}
          sx={{ mt: 1.5, fontWeight: 600, color: rg.accent }}
        >
          {showApiTable
            ? t("hideApiTable", "Hide API statistics")
            : t("viewApiTable", "View all API statistics →")}
        </Button>
      </PanelBox>

      <Collapse in={showApiTable} timeout={160} unmountOnExit>
        <Box sx={{ mt: 2.5 }}>
          <ApiStatisticsTable />
        </Box>
      </Collapse>
    </Box>
  );
};

export default StatisticsDashboard;
